import { useQuery } from '@apollo/client';
import { CKBox, CKSelect, CKTextField } from '@clearkit/react';
import { useState } from 'react';

import SalesforceLogo from '~/images/destinations/Salesforce.svg';
import { destinationTypeSalesforceCreateAccount } from '~/lib/destinations';
import { errorMessage, hasError } from '~/lib/graphql';
import { readableSingularSegmentType } from '~/lib/segments';
import Disconnected from '~/pages/destinations/components/Disconnected';
import FieldLabel from '~/pages/destinations/components/FieldLabel';
import FormHeader from '~/pages/destinations/components/FormHeader';
import TriggerType from '~/pages/destinations/components/TriggerType';

import query from './query.graphql';
import './styles.css';

function CreateExternalRecordsSalesforce({
  errors,
  refetch,
  accountConnectors,
  destination,
  _destinationOptions,
  segment,
  FormFooter,
}) {
  const accountConnector = accountConnectors.salesforce;
  const isNew = !destination;
  const initialConfig = isNew ? {} : destination.config;
  const humanizedSegmentType = readableSingularSegmentType(segment.type);

  const initialState = {
    traits: initialConfig.traits || [],
    triggerType: isNew ? 'segment_enter' : destination.triggerType,
  };

  const [traits, setTraits] = useState(initialState.traits);
  const [triggerType, setTriggerType] = useState(initialState.triggerType);
  const { data, loading } = useQuery(query);

  const updateAndMapTraits = (trait, { selectedItem }) => {
    let traitConfig = {};
    const { name } = trait;
    const { value, label } = selectedItem;
    const newTraits = traits.filter((oldTrait) => oldTrait.name !== name);

    traitConfig =
      trait.type === 'SELECT'
        ? { name, label, value }
        : { name, label, field: value };

    setTraits([...newTraits, traitConfig]);
  };

  const traitSelected = (selectedTraits, activeTrait) => {
    const selectedTrait = selectedTraits.find(
      (trait) => trait.name === activeTrait.name,
    );

    return (
      !!selectedTrait && {
        value: selectedTrait.value || selectedTrait.field,
        label: selectedTrait.label,
      }
    );
  };

  if (!accountConnector || accountConnector?.status !== 'active') {
    return (
      <Disconnected
        service="salesforce"
        onSuccess={refetch}
        Logo={SalesforceLogo}
        FormFooter={FormFooter}
      />
    );
  }

  if (loading) {
    return null;
  }

  return (
    <>
      <FormHeader
        customTitle={'Create account'}
        destinationType={destinationTypeSalesforceCreateAccount}
        destinationName="Salesforce"
        destinationFlowDescription={'Configuration Destination'}
        segmentName={segment.name}
        segmentCollection={segment.collection.name}
      />

      <>
        <CKBox
          className={'py-3 flex flex-1 rounded-none'}
          style={{ background: '#fafbfc' }}>
          <div className={'mx-6'}>
            <div className={'flex basis-1 items-start shrink-1 mr-5 flex-col'}>
              <span
                className={'text-base font-medium shrink-0'}
                style={{ color: '#27282a' }}>
                Trigger
              </span>
              <span
                className={
                  'text-ellipsis whitespace-nowrap overflow-hidden max-w-full text-sm'
                }
                style={{ color: '#596168', marginTop: '4px' }}>
                We will only create a new account if the website domain doesn't
                exist in your Salesforce.{' '}
                <a
                  href="https://help.clearbit.com/hc/en-us/articles/360023453694"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'underline' }}>
                  Learn more
                </a>
              </span>
            </div>

            <TriggerType
              className={'external-account-trigger-type'}
              errors={errors}
              handleChange={(_, val) => setTriggerType(val)}
              isDisabled
              triggerType={triggerType}
              triggerTypes={[
                {
                  value: 'segment_enter',
                  label: (
                    <span style={{ color: '#596168' }}>
                      When a {humanizedSegmentType} <strong>enters</strong> the
                      audience
                    </span>
                  ),
                },
              ]}
            />
          </div>

          <CKBox className={'p-6 mt-3 mx-6'}>
            {data.salesforceTraits.map((trait, idx) => (
              <div className={'mb-4 w-full'} key={idx}>
                <FieldLabel
                  for={`${trait.name}_selector`}
                  label={trait.name}
                  className={'mb-2 text-gray-600 text-base font-medium'}
                />
                {trait.type === 'SELECT' ? (
                  <>
                    <CKSelect
                      onSelectedItemChange={(e) => updateAndMapTraits(trait, e)}
                      itemToString={(item) => item.label}
                      items={trait.options}
                      placeholder={'Choose an option'}
                      selectedItem={traitSelected(traits, trait)}
                    />
                    {hasError(errors, `trait_${trait.name}`, false) && (
                      <div className="mt-1 text-red">
                        {errorMessage(errors, `trait_${trait.name}`, false)}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <CKSelect
                      onSelectedItemChange={(e) => updateAndMapTraits(trait, e)}
                      itemToString={(item) => item.label}
                      items={data.enrichmentAttributes}
                      placeholder={'Choose an option'}
                      selectedItem={traitSelected(traits, trait)}
                    />
                    {hasError(errors, `trait_${trait.name}`, false) && (
                      <div className="mt-1 text-red">
                        {errorMessage(errors, `trait_${trait.name}`, false)}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            <div className={'mb-4 w-full flex flex-col'}>
              <FieldLabel
                for={'website'}
                label={'Website'}
                className={'mb-2 text-gray-600 text-base font-medium'}
              />
              <CKTextField readOnly value={'Company Domain'} name={'website'} />
            </div>
          </CKBox>
        </CKBox>
        <FormFooter
          destinationConfig={{
            config: {
              traits,
            },
            triggerType,
          }}
        />
      </>
    </>
  );
}

CreateExternalRecordsSalesforce.propTypes = {
  destination: PropTypes.object,
  destinationOptions: PropTypes.object,
  errors: PropTypes.array.isRequired,
  refetch: PropTypes.func.isRequired,
  accountConnectors: PropTypes.shape({
    salesforce: PropTypes.object.isRequired,
  }),
  segment: PropTypes.object,
};

export default CreateExternalRecordsSalesforce;
